import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Divider, Stack, Typography } from '@mui/material';
import { StatusCodes } from 'http-status-codes';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import InternalCommentsCategories from './investigatorNotesTab/internalComments/categories';
import NoteCategory from './investigatorNotesTab/internalComments/noteCategory';

import ComposeIcon from '../../../../../assets/svg/composeMessage.svg';
import { axiosInstanceForTenantAPI } from '../../../../../assets/utils/axiosInstance';
import { isRESTAPIError } from '../../../../../assets/utils/helpers';
import { useAuth } from '../../../../../contexts/auth/authContext';
import {
  useAppDispatch,
  useAppSelector,
} from '../../../../../hooks/global/typedReduxHooks';
import ErrorType from '../../../../../interfaces/ErrorType';
import InternalCommentObject from '../../../../../interfaces/InternalCommentsObject';
import { updateReportInternalComments } from '../../../../../slices/reportDataSlice';
import { RootState } from '../../../../../utils/store';
import StandardTextEditor from '../../../../common/editor/standardTextEditor';
import ExpandButton from '../../../../reportSubmission/fullscreenDescription/buttons/expandButton';
import DescriptionDialog from '../../../../reportSubmission/fullscreenDescription/dialog/descriptionDialog';

const dateToString = (date: string) => {
  if (!isNaN(new Date(date).getDate())) {
    return new Date(date).toLocaleString([], {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    });
  } else return '--';
};

const bodyText1 = {
  color: 'black',
  fontWeight: 'bold',
  fontSize: '1rem',
};
const bodyText2 = {
  color: 'grey',
  fontWeight: '400',
  fontSize: '.9rem',
};

const InternalCommentsTab = () => {
  const REPORT_CLOSED_BOUNDARY = 3;
  const { reportId } = useParams();
  const [showMessageField, setShowMessageField] = useState<boolean>(false);
  const [content, setContent] = useState<string>('');

  const [error, setError] = useState<ErrorType>({
    errorType: false,
    message: '',
  });
  const [openDialog, setOpen] = useState<boolean>(false);
  const [addHeight, setAddHeight] = useState(0);

  const { slug } = useAuth();
  const [companySlug, setCompanySlug] = useState<string | null>(slug);
  const intl = useIntl();
  const internalComments = useAppSelector(
    (state: RootState) => state.reportData.report.internalComments,
  );
  const investigatorNoteCategories = useAppSelector(
    (state) => state.reports.investigatorNoteCategories,
  );
  const reportStatus = useAppSelector(
    (state) => state.reportData.report.status,
  );
  const userAccessRoles = useAppSelector(
    (state: RootState) => state.reportData.userAccessRoles,
  );
  const dispatch = useAppDispatch();
  const messagesEndRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (!slug) {
      setCompanySlug(localStorage.getItem('slug'));
    }
  }, [slug]);

  const createInternalComment = useCallback(
    async (contentText: string) => {
      setError({
        errorType: false,
        message: '',
      });

      try {
        const newdata = { content: contentText };
        const response = await axiosInstanceForTenantAPI(companySlug).post(
          `/report/${reportId}/internal-comments`,
          newdata,
        );

        const res = response.data;
        //set the new created comment in the Internal comment array
        const newComment: InternalCommentObject = {
          id: res.id,
          name: res.author?.full_name,
          content: res.content,
          createdAt: res.created_at,
          category: null,
        };
        dispatch(
          updateReportInternalComments([...internalComments, newComment]),
        );
        //set content to empty and hid message field
        setContent('');
        setShowMessageField(false);
        scrollToBottom();
      } catch (err: unknown) {
        let errorMessage = intl.formatMessage({
          defaultMessage: 'Error happened. We\'re currently fixing it.',
        });
        if (isRESTAPIError(err)) {
          if (`${err.response.status}` === String(StatusCodes.NOT_FOUND)) {
            errorMessage = intl.formatMessage({
              defaultMessage: 'Error happened, We\'re currently fixing it.',
            });
          } else if (
            `${err.response.status}` === String(StatusCodes.BAD_REQUEST)
          ) {
            errorMessage = intl.formatMessage({
              defaultMessage: 'Failure to process request',
            });
          } else if (
            `${err.response.status}` === String(StatusCodes.UNAUTHORIZED)
          ) {
            errorMessage = intl.formatMessage({
              defaultMessage: 'You are unauthorized to send a Internal comment',
            });
          } else if (
            `${err.response.status}` === String(StatusCodes.FORBIDDEN)
          ) {
            errorMessage = intl.formatMessage({
              defaultMessage: 'You cannot use this request',
            });
          } else if (
            `${err.response.status}` ===
            String(StatusCodes.INTERNAL_SERVER_ERROR)
          ) {
            errorMessage = intl.formatMessage({
              defaultMessage: 'Error happened in the server',
            });
          }
        }
        setError({
          errorType: true,
          message: errorMessage,
        });
      }
    },
    [companySlug, internalComments, reportId, intl, dispatch],
  );

  const textChangeHandler = (text: string) => {
    setContent(text);
  };

  const handleOpenDialog = () => {
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
  };

  const isTextEmpty = () => {
    return content.replace(/<[^>]*>?/gm, '').trim().length === 0;
  };

  const clickSendMessageHandler = useCallback(
    (text: string) => {
      if (text !== '') {
        createInternalComment(text);
        setOpen(false);
        setContent('');
        setAddHeight(0);
        setShowMessageField(false);
      }
    },
    [createInternalComment],
  );

  const SendButton = () => (
    <Button
      variant='contained'
      sx={{
        maxWidth: '150px',
        padding: '10px 30px',
        borderRadius: '50px',
        alignItems: 'center',
        bgcolor: '#2449FC',
        marginLeft: 'auto',
        textTransform: 'none',
        height: '34px',
      }}
      onClick={() => clickSendMessageHandler(content)}
      disabled={isTextEmpty()}
    >
      <FormattedMessage defaultMessage='Send' />
    </Button>
  );

  const CloseButton = () => (
    <Button
      variant='text'
      onClick={() => {
        setOpen(false);
        setAddHeight(0);
        setShowMessageField(false);
      }}
      sx={{
        height: '2rem',
        padding: '0.85rem',
        borderRadius: '4px',
        gap: '0.625rem',
        color: '#878787',
        '& .MuiButton-startIcon': {
          marginRight: '0px',
        },
        textTransform: 'none',
      }}
      disableElevation
      startIcon={<CloseIcon htmlColor='#878787' />}
    >
      <Typography
        sx={{
          fontFamily: 'Schibsted Grotesk',
          fontSize: '14px',
          fontWeight: 600,
          lineHeight: '17px',
          letterSpacing: '0em',
          color: '#878787',
          my: '0.5rem',
          display: { xs: 'none', sm: 'initial' },
        }}
      >
        <FormattedMessage defaultMessage='Close' />
      </Typography>
    </Button>
  );

  const activeCategories = investigatorNoteCategories.filter(
    (category) => category.selected,
  );

  return (
    <Box
      sx={{
        height: 'auto',
        position: 'relative',
      }}
    >
      <Box py={2}>
        <InternalCommentsCategories />
      </Box>
      <Box
        sx={{
          overflowY: 'auto',
          // height: ,
          height: showMessageField ? 'calc(10vh + ' + addHeight.toString() + 'px)' : '380px',
          scrollbarWidth: 'thin',
          paddingTop: '1rem',
          '::-webkit-scrollbar': {
            width: '10px',
          },
          '::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '::-webkit-scrollbar-thumb': {
            bgcolor: 'rgba(155, 155, 155, 0.5)',
            borderRadius: ' 20px',
            border: ' transparent',
          },
        }}
      >
        {Array.isArray(internalComments) && internalComments.length === 0 && (
          <Box>
            <Typography
              sx={{ ...bodyText1, textAlign: 'center', color: 'grey' }}
            >
              <FormattedMessage defaultMessage='No Notes Found' />
            </Typography>
          </Box>
        )}
        {Array.isArray(internalComments) &&
          internalComments
            .filter((comment) => {
              if (activeCategories.length === 0) return true;
              return activeCategories.find(
                (category) => category.id === comment.category?.id,
              );
            })
            .map((message, key) => {
              return (
                <>
                  <Stack
                    key={key}
                    gap={1.5}
                    sx={{
                      wordWrap: 'break-word',
                    }}
                  >
                    <Stack direction='row' justifyContent='space-between'>
                      <Stack>
                        <Typography sx={bodyText1}>{message.name}</Typography>
                        <Typography sx={{ ...bodyText2 }}>
                          {message ? dateToString(message.createdAt) : ''}{' '}
                          {message.isFromMeetingNotes ? ' via meeting' : ''}
                        </Typography>
                      </Stack>
                      <Stack justifyContent='center'>
                        {reportStatus < REPORT_CLOSED_BOUNDARY && (
                          <NoteCategory internalComment={message} />
                        )}
                      </Stack>
                    </Stack>
                    <div
                      dangerouslySetInnerHTML={{ __html: message.content }}
                      className='formatted-text-viewer'
                    />
                  </Stack>
                  {internalComments.length - 1 !== key && (
                    <Divider sx={{ marginY: '16px' }} />
                  )}
                </>
              );
            })}
        <Box ref={messagesEndRef} />

        {/* FAB button */}
        {!showMessageField && (
          <Button
            disabled={userAccessRoles.is_read_only_report}
            variant='contained'
            sx={{
              padding: '12px',
              gap: '8px',
              bgcolor: '#2449FC',
              position: 'absolute',
              right: 11,
              bottom: -10,
              textTransform: 'none',
              borderRadius: '12px',
              boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.20)',
            }}
            onClick={() => {
              setShowMessageField(true);
              setTimeout(() => {
                scrollToBottom();
              }, 100);
            }}
          >
            <Box
              component='img'
              sx={{ height: '20px' }}
              src={ComposeIcon}
            ></Box>
            <Typography fontSize={14}>
              <FormattedMessage defaultMessage='Compose' />
            </Typography>
          </Button>
        )}
      </Box>

      {error.errorType && (
        <Typography
          color='error'
          variant='body2'
          marginY={1}
          marginRight={2}
          textAlign='end'
        >
          {error.message}
        </Typography>
      )}

      {showMessageField && (
        <Box
          sx={{
            width: 'inherit',
            marginX: '-16px',
            borderRadius: '6px',
            border: '1px solid #DADADA',
            '&:focus-within': {
              border: '1px solid #181818',
            },
            backgroundColor: 'white',
            position: 'sticky',
            left: '1%',
            right: '1%',
            bottom: '5%',
          }}
        >
          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='center'
            sx={{
              padding: '4px 12px',
              backgroundColor: '#F6F6F6',
              borderRadius: '6px 6px 0px 0px',
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Schibsted Grotesk',
                color: '#878787',
              }}
            ></Typography>
            <Stack direction='row' alignItems='center' spacing={0}>
              <ExpandButton
                handleButtonClicked={handleOpenDialog}
                variant='dark'
              />
              <CloseButton />
            </Stack>
          </Stack>
          <DescriptionDialog
            text={content}
            onChangeText={textChangeHandler}
            open={openDialog}
            setClose={handleCloseDialog}
            variant='message'
            messageButton={<SendButton />}
            closeButton={<CloseButton />}
            hideTitle
          />
          <StandardTextEditor
            text={content}
            onChangeText={textChangeHandler}
            customPlaceholder={intl.formatMessage({
              defaultMessage: 'Type here...',
            })}
            variant='message'
            messageButton={<SendButton />}
          />
        </Box>
      )}
    </Box>
  );
};

export default InternalCommentsTab;
